// src/components/onboarding/AgreementsSection.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  downloadDocument,
  getMyDocuments,
  openBlob,
  responseMessage,
} from "../../shared/_services/onboarding.services";
import {
  AGREEMENT_DOWNLOAD,
  DOCS_PREPARING,
  DOCS_SECTION,
  LOAD_FAILED,
  PILL_DELIVERED,
  PILL_SIGNED,
  TRY_AGAIN,
} from "./onboardingCopy";
import StarsIcon from "../../styles/StarsIcon";
import { Badge, Button, Panel } from "../ui";
import { fullDate, pdfName } from "./onboardingFormat";

function DocRow({ row, onDownload, busyId }) {
  return (
    <li
      data-slot="onb-doc"
      className="tw-border-0 tw-border-b tw-border-solid tw-border-divider last:tw-border-b-0"
    >
      <div
        data-slot="onb-doc-line"
        className="tw-flex tw-items-center tw-gap-3 tw-py-3"
      >
        <div data-slot="onb-doc-main" className="tw-min-w-0 tw-flex-1">
          <div
            data-slot="onb-doc-name"
            className="tw-text-sm tw-font-semibold tw-text-text"
          >
            {row.name}
          </div>
          <div data-slot="onb-muted" className="tw-text-sm tw-text-text-muted">
            {`${fullDate(row.at)} · ${row.versionLabel}`}
          </div>
        </div>
        <Badge tone={row.kind === "signed" ? "accent" : "neutral"}>
          {row.kind === "signed" ? PILL_SIGNED : PILL_DELIVERED}
        </Badge>
        {row.pdfPending ? (
          <span
            data-slot="onb-muted"
            className="tw-whitespace-nowrap tw-text-sm tw-text-text-muted"
          >
            {DOCS_PREPARING}
          </span>
        ) : (
          row.canDownload && (
            <Button
              variant="quiet"
              size="sm"
              aria-label={`${AGREEMENT_DOWNLOAD}: ${row.name} ${row.versionLabel}`}
              disabled={busyId === row.id}
              onClick={() => onDownload(row)}
            >
              <StarsIcon
                name="download"
                className="tw-h-4 tw-w-4 tw-shrink-0"
              />
              {AGREEMENT_DOWNLOAD}
            </Button>
          )
        )}
      </div>
      {row.children?.length > 0 && (
        <ul
          data-slot="onb-doc-children"
          className="tw-m-0 tw-list-none tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-p-0 tw-pl-6"
        >
          {row.children.map((child) => (
            <DocRow
              key={child.id}
              row={child}
              onDownload={onDownload}
              busyId={busyId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// You › Documents: the client's signed agreements and delivered disclosures
// (GET /documents/me `agreements`), above the uploads list. Renders nothing
// for a client who has none.
export default function AgreementsSection() {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await getMyDocuments();
      setRows(res.data.agreements || []);
    } catch (_) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (row) => {
    setBusyId(row.id);
    try {
      const res = await downloadDocument(row.id);
      openBlob(res.data, pdfName(row.name, row.versionLabel));
    } catch (err) {
      enqueueSnackbar((await responseMessage(err)) || LOAD_FAILED, {
        variant: "error",
      });
    } finally {
      setBusyId(null);
    }
  };

  if (failed) {
    return (
      <div
        data-slot="onb-docs"
        className="tw-mb-4 tw-text-sm tw-text-text"
        role="alert"
      >
        <span>{LOAD_FAILED}</span>{" "}
        <Button variant="text" size="sm" onClick={load}>
          {TRY_AGAIN}
        </Button>
      </div>
    );
  }
  if (!rows || rows.length === 0) return null;

  return (
    <section
      data-slot="onb-docs"
      className="tw-mb-4"
      aria-labelledby="onb-docs-heading"
    >
      <h3
        id="onb-docs-heading"
        data-slot="onb-h3"
        className="tw-m-0 tw-mb-2 tw-text-lg tw-font-bold tw-text-text"
      >
        {DOCS_SECTION}
      </h3>
      <Panel as="ul" className="tw-m-0 tw-list-none tw-py-0 tw-px-4">
        {rows.map((row) => (
          <DocRow
            key={row.id}
            row={row}
            onDownload={download}
            busyId={busyId}
          />
        ))}
      </Panel>
    </section>
  );
}
